import React from 'react';

// Content of the ☰ / profile slide-in panel on phones and tablets, shared by
// every module header: a "Welcome, NAME" header, then cards for
// Notifications, Exhibition Details, For Assistance and any extra content.
// Each header passes only the sections it shows. Styled by the .mip-* rules
// in public/css/responsive-fixes.css.
//
// Props
//   name            bold line in the header (user / company / exhibition name)
//   welcome         small line above the name (default "Welcome,"; pass '' to hide)
//   onClose         closes the panel
//   compactHead     no coloured header, only a round close button (team member info panel)
//   notifications   array of { id, title, unread } - leave undefined to hide the card
//   onNotificationClick(n)
//   details         { dates, location, space, rows: [{ icon, label, value }] } - undefined hides the card
//   assistance      { phones: [], emails: [] } - undefined or empty hides the card
//   children        extra cards (e.g. account options) shown at the end
const MobileInfoPanel = ({ name, welcome = 'Welcome,', onClose, compactHead, notifications, onNotificationClick, details, assistance, children }) => {
    const phones = (assistance && assistance.phones || []).filter(Boolean);
    const emails = (assistance && assistance.emails || []).filter(Boolean);

    const detailRows = [];
    if (details) {
        if (details.dates) detailRows.push({ icon: 'fa-calendar', tone: 'teal', label: 'Dates', value: details.dates, strong: true });
        if (details.location) detailRows.push({ icon: 'fa-map-marker', tone: 'blue', label: 'Location', value: details.location });
        if (details.space) detailRows.push({ icon: 'fa-object-group', tone: 'purple', label: 'Space Details', value: details.space });
        (details.rows || []).forEach(r => detailRows.push({ tone: 'teal', ...r }));
    }

    return (
        <div className="mip">
            {compactHead ? (
                <div className="mip-compact-head">
                    <button type="button" className="info-panel-close" aria-label="Close" onClick={onClose}>
                        <i className="fa fa-times"></i>
                    </button>
                </div>
            ) : (
                <div className="mip-head">
                    <span className="mip-avatar"><i className="fa fa-user"></i></span>
                    <div className="mip-head-text">
                        {welcome && <div className="mip-welcome">{welcome}</div>}
                        <div className="mip-name">{name}</div>
                    </div>
                    <button type="button" className="mip-close" aria-label="Close" onClick={onClose}>&times;</button>
                </div>
            )}

            <div className="mip-body">
                {notifications && (
                    <div className="mip-card">
                        <div className="mip-card-title">
                            <i className="fa fa-bell"></i>
                            <span>Notifications</span>
                            {notifications.some(n => n.unread) && <span className="mip-count">{notifications.filter(n => n.unread).length}</span>}
                        </div>
                        {notifications.length === 0 ? (
                            <div className="mip-empty">
                                <span className="mip-empty-icon"><i className="fa fa-bell-o"></i></span>
                                <div>No notifications</div>
                            </div>
                        ) : (
                            <div className="mip-list">
                                {notifications.slice(0, 3).map(n => (
                                    <div
                                        key={n.id}
                                        className={`mip-notif${n.unread ? ' unread' : ''}`}
                                        onClick={() => onNotificationClick && onNotificationClick(n)}
                                    >
                                        <span className="mip-dot"></span>
                                        <span dangerouslySetInnerHTML={{ __html: n.title }}></span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {detailRows.length > 0 && (
                    <div className="mip-card">
                        <div className="mip-card-title">
                            <i className="fa fa-calendar-check-o"></i>
                            <span>Exhibition Details</span>
                        </div>
                        <div className="mip-list">
                            {detailRows.map((r, i) => (
                                <div className="mip-row" key={i}>
                                    <span className={`mip-row-icon mip-${r.tone}`}><i className={`fa ${r.icon}`}></i></span>
                                    <div className="mip-row-text">
                                        <div className="mip-label">{r.label}</div>
                                        <div className={`mip-value${r.strong ? ' mip-value-strong' : ''}`}>{r.value}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {(phones.length > 0 || emails.length > 0) && (
                    <div className="mip-card">
                        <div className="mip-card-title">
                            <i className="fa fa-headphones"></i>
                            <span>For Assistance</span>
                        </div>
                        <div className="mip-list">
                            {phones.length > 0 && (
                                <div className="mip-row">
                                    <span className="mip-row-icon mip-teal"><i className="fa fa-phone"></i></span>
                                    <div className="mip-row-text">
                                        <div className="mip-label">Phone</div>
                                        {phones.map((p, i) => <div className="mip-value" key={i}><a href={`tel:${String(p).replace(/[^\d+]/g, '')}`}>{p}</a></div>)}
                                    </div>
                                </div>
                            )}
                            {emails.length > 0 && (
                                <div className="mip-row">
                                    <span className="mip-row-icon mip-blue"><i className="fa fa-envelope-o"></i></span>
                                    <div className="mip-row-text">
                                        <div className="mip-label">Email</div>
                                        {emails.map((e, i) => <div className="mip-value mip-break" key={i}><a href={`mailto:${e}`}>{e}</a></div>)}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {children}
            </div>
        </div>
    );
};

// Card with Change Password / Sign out rows, for headers that show them in the panel
export const MobileAccountCard = ({ onChangePassword, onSignOut }) => (
    <div className="mip-card mip-account">
        {onChangePassword && (
            <button type="button" className="mobile-account-item" onClick={onChangePassword}>
                <i className="fa fa-lock"></i> Change Password
            </button>
        )}
        {onSignOut && (
            <button type="button" className="mobile-account-item mobile-account-logout" onClick={onSignOut}>
                <i className="fa fa-sign-out"></i> Sign out
            </button>
        )}
    </div>
);

export default MobileInfoPanel;
